import { universalApiService } from './universalApiService';
import { useUserPermissionsStore } from '../stores/userPermissionsStore';

/**
 * User Groups Service - FIXED VERSION with correct permission processing
 */
class UserGroupsService {

  /**
   * Fetch user groups and permissions using proper Azure AD group matching - FIXED VERSION
   * FIXED: Now correctly processes backend permission format
   */
  async fetchUserGroupsAndPermissions(userAzureGroupIds: string[] = []): Promise<any> {
    try {
      // FIXED User Groups Service: Starting fetch
      // Input Azure Group IDs available
      
      // Step 1: Get all user groups to find current user's group
      // Fetching user groups
      const userGroupsData = await universalApiService.get('user-groups/');
      // User groups data received

      // Step 2: Match user's Azure AD group IDs with available groups
      const userGroupsResponse = userGroupsData as any;
      // Raw user groups response structure available
      
      // Get all available groups (try multiple possible response structures)
      const allGroups = userGroupsResponse?.data?.user_groups || 
                       userGroupsResponse?.data || 
                       userGroupsResponse?.user_groups || 
                       userGroupsResponse || 
                       [];
      
      // Available groups found
      
      // Find matching group by azure_group_id
      let currentUserGroup = null;
      let matchingGroupIndex = -1;
      
      if (userAzureGroupIds.length > 0) {
        // Attempting to match user Azure group IDs with available groups
        
        // Try to match user's Azure AD groups with available groups
        for (let i = 0; i < userAzureGroupIds.length; i++) {
          const azureGroupId = userAzureGroupIds[i];
          // Checking Azure Group ID
          
          for (let j = 0; j < allGroups.length; j++) {
            const group = allGroups[j];
            const groupAzureId = group.azure_group_id || group.azureGroupId || group.groupId;
            
            // Comparing with group
            
            if (groupAzureId === azureGroupId) {
              currentUserGroup = group;
              matchingGroupIndex = j;
              // MATCH FOUND! Group matches Azure ID
              break;
            }
          }
          
          if (currentUserGroup) {
            break; // Stop searching once we find a match
          }
        }
      } else {
        // No Azure group IDs provided for matching
      }
      
      if (!currentUserGroup) {
        const errorMsg = `User is not authorized to access this system. Your Azure AD groups do not match any configured user groups for this environment. If you believe this is an error, please contact your administrator. Available groups: ${allGroups.length}, User Azure IDs: [${userAzureGroupIds.join(', ')}]`;
        console.error('❌ ' + errorMsg);
        throw new Error('User is not authorized to access this system. Your Azure AD groups do not match any configured user groups for this environment. If you believe this is an error, please contact your administrator.');
      }

      // FIXED: Extract group ID using correct field name from backend
      const groupId = currentUserGroup.user_group_id || currentUserGroup.id || currentUserGroup.group_id || currentUserGroup.groupId;
      
      if (!groupId) {
        console.error('❌ Group ID not found in user group object:', currentUserGroup);
        console.error('❌ Available fields:', Object.keys(currentUserGroup));
        throw new Error('Group ID not found in selected user group');
      }
      
      // Selected user group available

      // Step 3: Get permission matrix for the user's group with better error handling
      // Fetching permission matrix for group ID
      const matrixEndpoint = `user-groups/${groupId}/permissions/matrix`;
      // Matrix API endpoint and Full URL available
      
      let permissionsData;
      try {
        // Making matrix API call
        permissionsData = await universalApiService.get(matrixEndpoint);
        // Permission matrix received successfully
      } catch (matrixError) {
        console.error('❌ Matrix API call failed:', {
          endpoint: matrixEndpoint,
          groupId: groupId,
          fullURL: `http://localhost:8000/${matrixEndpoint}`,
          error: matrixError
        });
        throw new Error(`Matrix API failed for group ${groupId}: ${matrixError}`);
      }

      // Step 4: FIXED - Process backend permissions format correctly
      const store = useUserPermissionsStore.getState();
      store.setUserGroup(currentUserGroup);
      
      const permissionsResponse = permissionsData as any;
      
      // Processing permissions data
      
      // FIXED: Extract permissions array from backend response
      const permissionsArray = permissionsResponse.data?.permissions || [];
      
      if (!Array.isArray(permissionsArray)) {
        console.error('❌ Expected permissions array, got:', typeof permissionsArray, permissionsArray);
        throw new Error('Invalid permissions format from backend - expected array');
      }
      
      // FIXED: Convert backend format to frontend format
      const permissionsObject: Record<string, boolean> = {};
      permissionsArray.forEach((perm: any) => {
        if (perm.permission_key && typeof perm.has_permission === 'boolean') {
          permissionsObject[perm.permission_key] = perm.has_permission;
        }
      });
      
      // FIXED: Converted permissions correctly
      
      // Convert to array format for Zustand store (keeping existing store format)
      const permissionsArrayForStore = Object.entries(permissionsObject).map(([name, value]) => ({
        id: 0,
        name,
        description: name,
        category: name.split('_')[0]
      }));
      
      store.setPermissions(permissionsArrayForStore);
      store.setError(null);
      
      // Updated Zustand store with user group and permissions
      // FIXED User Groups Service: Fetch completed successfully
      // Permissions should now be correctly processed

      // Normalize the user group object to match frontend expectations
      const normalizedUserGroup = {
        ...currentUserGroup,
        displayName: currentUserGroup.display_name || 
                     currentUserGroup.displayName || 
                     currentUserGroup.group_name || 
                     'Unknown Group'
      };

      return {
        userGroup: normalizedUserGroup, // Return normalized object with displayName field
        permissions: permissionsObject, // Return the correctly formatted permissions
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error('❌ FIXED User Groups Service: Fetch failed:', error);
      
      // Update Zustand store with error
      const store = useUserPermissionsStore.getState();
      store.setError(error instanceof Error ? error.message : String(error));
      
      throw error;
    }
  }
}

// Export singleton instance
export const userGroupsService = new UserGroupsService();
export default userGroupsService;
